import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from './context/AuthContext';

// Layouts
import TopLevel from './components/layout/TopLevel';
import PublicLayout from './components/public/PublicLayout';
import PublicPortalPage from './components/public/PublicPortalPage';
import PublicJobDetail from './components/public/PublicJobDetail';
import ProductLandingPage from './components/public/ProductLandingPage';
import LegacyPublicJobRedirect from './components/public/LegacyPublicJobRedirect';
import PublicTrainingRecordingPage from './components/public/PublicTrainingRecordingPage';

// Auth pages
import Login from './modules/auth/Login';
import Signup from './modules/auth/Signup';
import OTPVerification from './modules/auth/OTPVerification';
import EmailVerification from './modules/auth/EmailVerification';
import DemoPreviewBootstrap from './modules/auth/DemoPreviewBootstrap';
import MarketingPreviewBootstrap from './modules/auth/MarketingPreviewBootstrap';

// Dashboard
import Dashboard from './modules/dashboard/Dashboard';

// Core modules
import ContactsList from './modules/contacts/ContactsList';
import ContactForm from './modules/contacts/ContactForm';
import ContactDetails from './modules/contacts/ContactDetails';

import AccountsList from './modules/accounts/AccountsList';
import AccountForm from './modules/accounts/AccountForm';
import AccountDetails from './modules/accounts/AccountDetails';

import JobsList from './modules/jobs/JobsList';
import JobForm from './modules/jobs/JobForm';
import JobDetails from './modules/jobs/JobDetails';

import DocumentsList from './modules/documents/DocumentsList';
import DocumentViewPage from './modules/documents/DocumentViewPage';

// HR modules
import EmployeesList from './modules/employees/EmployeesList';
import EmployeeForm from './modules/employees/EmployeeForm';
import EmployeeDetails from './modules/employees/EmployeeDetails';

import TimesheetsList from './modules/timesheets/TimesheetsList';
import TimesheetForm from './modules/timesheets/TimesheetForm';

import TrainingList from './modules/training/TrainingList';
import TrainingMaterials from './modules/training/TrainingMaterials';
import TrainingCatalog from './modules/training/TrainingCatalog';
import TrainingCatalogDetail from './modules/training/TrainingCatalogDetail';
import UserTraining from './modules/training/UserTraining';
import TrainingInstructorDashboard from './modules/training/TrainingInstructorDashboard';
import TrainingLiveRoom from './modules/training/TrainingLiveRoom';
import TrainingLearn from './modules/training/TrainingLearn';
import StandaloneExamCatalog from './modules/training/StandaloneExamCatalog';
import TrainingExamTake from './modules/training/TrainingExamTake';
import TrainingQuestionBank from './modules/training/TrainingQuestionBank';
import GroupDiscussions from './modules/training/GroupDiscussions';

// Admin modules
import UsersList from './modules/admin/UsersList';
import UserForm from './modules/admin/UserForm';
import UserDetail from './modules/admin/UserDetail';
import PendingApprovals from './modules/admin/PendingApprovals';
import DomainStatus from './modules/admin/DomainStatus';
import AdminCompanyDomains from './modules/admin/AdminCompanyDomains';
import AdminUserIdPatterns from './modules/admin/AdminUserIdPatterns';
import UserCreationTemplates from './modules/admin/UserCreationTemplates';
import DbBackupAdmin from './modules/admin/DbBackupAdmin';
import DocumentBackupAdmin from './modules/admin/DocumentBackupAdmin';
import SystemInfoPage from './modules/admin/SystemInfoPage';
import ImagesAdmin from './modules/admin/ImagesAdmin';
import RolesList from './modules/admin/RolesList';
import RoleForm from './modules/admin/RoleForm';
import RoleDetail from './modules/admin/RoleDetail';
import GroupsList from './modules/admin/GroupsList';
import GroupForm from './modules/admin/GroupForm';
import GroupDetail from './modules/admin/GroupDetail';
import Settings from './modules/admin/Settings';
import VisibilitySetup from './modules/admin/VisibilitySetup';
import CustomizeApplicationSettings from './modules/admin/CustomizeApplicationSettings';
import CustomFieldsAdmin from './modules/admin/CustomFieldsAdmin';
import ModuleRelationshipsAdmin from './modules/admin/ModuleRelationshipsAdmin';
import AdminAuditTrail from './modules/admin/AdminAuditTrail';
import AdminAllStatusesRouter from './modules/admin/AdminAllStatusesRouter';
import AdminSeedData from './modules/admin/AdminSeedData';
import DemoUsersActualView from './modules/admin/DemoUsersActualView';
import UserAccessMap from './modules/admin/UserAccessMap';
import AdminArchivedData from './modules/admin/AdminArchivedData';
import AdminDataClearance from './modules/admin/AdminDataClearance';
import AdminDataBackups from './modules/admin/AdminDataBackups';
import AdminDeletedData from './modules/admin/AdminDeletedData';
import AccountDuplicateRequests from './modules/admin/AccountDuplicateRequests';
import MyAccountStatus from './modules/accounts/MyAccountStatus';
import EmployeeJobRoles from './modules/admin/EmployeeJobRoles';
import EmployeeTitles from './modules/admin/EmployeeTitles';
import EmployeeOfferedTitles from './modules/admin/EmployeeOfferedTitles';

// Profile
import Profile from './modules/profile/Profile';

// Punch Card
import PunchCard from './modules/punchcard/PunchCard';

// Activities
import MyActivities from './modules/activities/MyActivities';
import AllActivities from './modules/activities/AllActivities';

// Reports
import PunchCardReport from './modules/reports/PunchCardReport';
import JobsReport from './modules/reports/JobsReport';
import ActivitiesReport from './modules/reports/ActivitiesReport';
import EmpIdleTimeReport from './modules/reports/EmpIdleTimeReport';
import MyIdleTimeReport from './modules/reports/MyIdleTimeReport';
import MyTeamIdleTimeReport from './modules/reports/MyTeamIdleTimeReport';

// Email Extractor
import EmailExtractorDashboard from './modules/email-extractor/EmailExtractorDashboard';
import ExtractedEmails from './modules/email-extractor/ExtractedEmails';
import DomainExclusions from './modules/email-extractor/DomainExclusions';
import ManualEntry from './modules/email-extractor/ManualEntry';
import ExtractionHistory from './modules/email-extractor/ExtractionHistory';
import PstUpload from './modules/email-extractor/PstUpload';
import EmailProviders from './modules/email-extractor/EmailProviders';
import GsaCompanies from './modules/extracted-data/GsaCompanies';
import UsUniversities from './modules/extracted-data/UsUniversities';

// Bench Sales
import BenchesList from './modules/bench-sales/BenchesList';
import BenchDetails from './modules/bench-sales/BenchDetails';
import AvailableCandidates from './modules/bench-sales/AvailableCandidates';

// Digital Media
import SocialMediaRegistration from './modules/digital-media/SocialMediaRegistration';
import MyAccounts from './modules/digital-media/MyAccounts';

import EmployeeCamera from './modules/camera/EmployeeCamera';
import AdminCameraDashboard from './modules/camera/AdminCameraDashboard';
import ChatHub from './modules/chat/ChatHub';
import TeamCameraStream from './modules/my-team/TeamCameraStream';
import MyTeamJobs from './modules/my-team/MyTeamJobs';
import MyTeamDashboard from './modules/my-team/MyTeamDashboard';
import MyTeamReportees from './modules/my-team/MyTeamReportees';

import HelpDeskMyTickets from './modules/helpdesk/HelpDeskMyTickets';
import HelpDeskTeamTickets from './modules/helpdesk/HelpDeskTeamTickets';
import HelpDeskNewTicket from './modules/helpdesk/HelpDeskNewTicket';
import HelpDeskJobSupportSeeker from './modules/helpdesk/HelpDeskJobSupportSeeker';
import HelpDeskJobSupportProvider from './modules/helpdesk/HelpDeskJobSupportProvider';
import HelpDeskTicketDetail from './modules/helpdesk/HelpDeskTicketDetail';
import HelpDeskQueue from './modules/helpdesk/HelpDeskQueue';
import HelpContacts from './modules/help/HelpContacts';
import HelpProduct from './modules/help/HelpProduct';
import AdminHelpSetup from './modules/admin/AdminHelpSetup';
import AdminHelpdeskSeverities from './modules/admin/AdminHelpdeskSeverities';
import TrainingAdministration from './modules/admin/TrainingAdministration';
import ProcessH1List from './modules/immigration/ProcessH1List';
import ProcessB1List from './modules/immigration/ProcessB1List';
import ProcessGCList from './modules/immigration/ProcessGCList';
import EverifyExistingList from './modules/immigration/EverifyExistingList';
import EverifyNewList from './modules/immigration/EverifyNewList';

import OkrMy from './modules/okr/OkrMy';
import OkrObjectivesList from './modules/okr/OkrObjectivesList';
import OkrObjectiveEditor from './modules/okr/OkrObjectiveEditor';
import OkrObjectiveDetail from './modules/okr/OkrObjectiveDetail';
import OkrCategories from './modules/okr/OkrCategories';
import KeyResultsMatrix from './modules/okr/KeyResultsMatrix';
import OkrTargetFrequencySettings from './modules/okr/OkrTargetFrequencySettings';
import OkrEmployeeMap from './modules/okr/OkrEmployeeMap';
import MyPtoApply from './modules/pto/MyPtoApply';
import TeamPtoApprovals from './modules/pto/TeamPtoApprovals';

const externalTrainingUi = (process.env.REACT_APP_EXTERNAL_TRAINING_UI || '').trim();

// Protected Route Component
const BENCH_SALES_MODULES = ['bench_sales', 'contacts'];

const ProtectedRoute = ({ children, permission, requireAdmin, anyModule }) => {
  const { isAuthenticated, loading, hasPermission, hasModuleAccess, hasAnyModuleAccess, isAdmin } = useAuth();

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        bgcolor="background.default"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (permission) {
    const hasAccess = Array.isArray(permission)
      ? hasPermission(permission)
      : permission.includes('_')
        ? hasPermission(permission)
        : hasModuleAccess(permission);

    if (!hasAccess) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
};

// Public Route Component (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        bgcolor="background.default"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

/** Unknown paths: send guests to login instead of bouncing through /dashboard. */
const DefaultRedirect = () => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="background.default"
      >
        <CircularProgress />
      </Box>
    );
  }
  return <Navigate to={isAuthenticated ? '/dashboard' : '/'} replace />;
};

function App() {
  return (
    <Routes>
      {/* One-time demo preview handoff (same-origin new window); not wrapped in TopLevel */}
      <Route path="/demo-preview-entry/:bootId" element={<DemoPreviewBootstrap />} />
      <Route path="/marketing-preview-entry/:bootId" element={<MarketingPreviewBootstrap />} />

      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
      <Route path="/verify-otp" element={<PublicRoute><OTPVerification /></PublicRoute>} />
      <Route path="/verify-email" element={<PublicRoute><EmailVerification /></PublicRoute>} />

      {/* Marketing landing (product overview) */}
      <Route path="/" element={<ProductLandingPage />} />

      {/* Public query portal — jobs, training, exams (no login) */}
      <Route path="/query" element={<PublicLayout portalBasePath="/query" />}>
        <Route index element={<PublicPortalPage />} />
        <Route path="jobs" element={<Navigate to="/query#portal-jobs" replace />} />
        <Route path="jobs/:id" element={<PublicJobDetail />} />
        <Route path="training/recordings/:id" element={<PublicTrainingRecordingPage />} />
      </Route>

      {/* Legacy /public URLs → /query */}
      <Route path="/public" element={<Navigate to="/query" replace />} />
      <Route path="/public/jobs" element={<Navigate to="/query#portal-jobs" replace />} />
      <Route path="/public/jobs/:id" element={<LegacyPublicJobRedirect />} />

      {/* App shell: authenticated users only (pathless layout — does not capture `/`) */}
      <Route element={<TopLevel />}>
        
        {/* Dashboard */}
        <Route path="dashboard" element={<Dashboard />} />

        {/* Contacts */}
        <Route path="contacts" element={<ProtectedRoute permission="contacts"><ContactsList /></ProtectedRoute>} />
        <Route path="contacts/new" element={<ProtectedRoute permission="contacts_add"><ContactForm /></ProtectedRoute>} />
        <Route path="contacts/:id" element={<ProtectedRoute permission="contacts"><ContactDetails /></ProtectedRoute>} />
        <Route path="contacts/:id/edit" element={<ProtectedRoute permission="contacts_modify"><ContactForm /></ProtectedRoute>} />

        {/* Accounts */}
        <Route path="accounts" element={<ProtectedRoute permission="accounts"><AccountsList /></ProtectedRoute>} />
        <Route path="accounts/new" element={<ProtectedRoute permission="accounts_add"><AccountForm /></ProtectedRoute>} />
        <Route path="accounts/:id" element={<ProtectedRoute permission="accounts"><AccountDetails /></ProtectedRoute>} />
        <Route path="accounts/:id/edit" element={<ProtectedRoute permission="accounts_modify"><AccountForm /></ProtectedRoute>} />

        {/* Jobs */}
        <Route path="jobs" element={<ProtectedRoute permission="jobs"><JobsList /></ProtectedRoute>} />
        <Route path="jobs/new" element={<ProtectedRoute permission="jobs_add"><JobForm /></ProtectedRoute>} />
        <Route path="jobs/:id" element={<ProtectedRoute permission="jobs"><JobDetails /></ProtectedRoute>} />
        <Route path="jobs/:id/edit" element={<ProtectedRoute permission="jobs_modify"><JobForm /></ProtectedRoute>} />

        {/* Documents */}
        <Route path="documents/:id/view" element={<ProtectedRoute permission="documents"><DocumentViewPage /></ProtectedRoute>} />
        <Route path="documents" element={<ProtectedRoute permission="documents"><DocumentsList /></ProtectedRoute>} />

        {/* Employees */}
        <Route path="employees" element={<ProtectedRoute requireAdmin><EmployeesList /></ProtectedRoute>} />
        <Route path="employees/new" element={<ProtectedRoute requireAdmin><EmployeeForm /></ProtectedRoute>} />
        <Route path="employees/:id" element={<ProtectedRoute requireAdmin><EmployeeDetails /></ProtectedRoute>} />
        <Route path="employees/:id/edit" element={<ProtectedRoute requireAdmin><EmployeeForm /></ProtectedRoute>} />

        {/* Timesheets */}
        <Route path="timesheets" element={<ProtectedRoute permission="timesheets"><TimesheetsList /></ProtectedRoute>} />
        <Route path="timesheets/new" element={<ProtectedRoute permission="timesheets_add"><TimesheetForm /></ProtectedRoute>} />
        <Route path="timesheets/:id" element={<ProtectedRoute permission="timesheets"><TimesheetForm /></ProtectedRoute>} />

        {/* Training (omit in bundle routes when served from erp4c-training-module-frontend) */}
        {!externalTrainingUi && (
          <>
            <Route path="training" element={<ProtectedRoute permission="training"><TrainingList /></ProtectedRoute>} />
            <Route path="training/catalog" element={<ProtectedRoute permission="training"><TrainingCatalog /></ProtectedRoute>} />
            <Route
              path="training/standalone-exams"
              element={<ProtectedRoute permission="training"><StandaloneExamCatalog /></ProtectedRoute>}
            />
            <Route
              path="training/exam/:examId/take"
              element={<ProtectedRoute permission="training"><TrainingExamTake /></ProtectedRoute>}
            />
            <Route
              path="training/question-bank"
              element={<ProtectedRoute permission="training"><TrainingQuestionBank /></ProtectedRoute>}
            />
            <Route
              path="training/group-discussions"
              element={<ProtectedRoute permission="training"><GroupDiscussions /></ProtectedRoute>}
            />
            <Route
              path="training/group-discussions/:id"
              element={<ProtectedRoute permission="training"><GroupDiscussions /></ProtectedRoute>}
            />
            <Route path="training/catalog/category/:categoryId" element={<ProtectedRoute permission="training"><TrainingCatalogDetail /></ProtectedRoute>} />
            <Route path="training/my-training" element={<ProtectedRoute permission="training"><UserTraining /></ProtectedRoute>} />
            <Route path="training/instructor" element={<ProtectedRoute permission="training"><TrainingInstructorDashboard /></ProtectedRoute>} />
            <Route path="training/live/:sessionId" element={<ProtectedRoute permission="training"><TrainingLiveRoom /></ProtectedRoute>} />
            <Route path="training/learn/:enrollmentId" element={<ProtectedRoute permission="training"><TrainingLearn /></ProtectedRoute>} />
            <Route path="training/admin" element={<ProtectedRoute permission="training_add"><TrainingMaterials /></ProtectedRoute>} />
            <Route path="training/materials" element={<ProtectedRoute permission="training_add"><TrainingMaterials /></ProtectedRoute>} />
            <Route path="training/exams" element={<Navigate to="/training/admin?tab=exams" replace />} />
          </>
        )}

        {/* Admin - Users */}
        <Route path="admin/users" element={<ProtectedRoute permission="users"><UsersList /></ProtectedRoute>} />
        <Route path="admin/users/new" element={<ProtectedRoute permission="users_add"><UserForm /></ProtectedRoute>} />
        <Route path="admin/users/:id/edit" element={<ProtectedRoute permission="users_modify"><UserForm /></ProtectedRoute>} />
        <Route path="admin/users/:id" element={<ProtectedRoute permission="users"><UserDetail /></ProtectedRoute>} />

        {/* Admin - Registration Management */}
        <Route path="admin/pending-approvals" element={<ProtectedRoute permission="users"><PendingApprovals /></ProtectedRoute>} />
        <Route path="admin/domain-status" element={<ProtectedRoute permission="users"><DomainStatus /></ProtectedRoute>} />
        <Route path="admin/blocked-domains" element={<Navigate to="/admin/domain-status" replace />} />
        <Route path="admin/company-domains" element={<ProtectedRoute permission="users"><AdminCompanyDomains /></ProtectedRoute>} />
        <Route path="admin/user-id-patterns" element={<ProtectedRoute permission="users"><AdminUserIdPatterns /></ProtectedRoute>} />
        <Route path="admin/user-access-map" element={<ProtectedRoute permission="users"><UserAccessMap /></ProtectedRoute>} />
        <Route
          path="admin/user-creation-templates"
          element={<ProtectedRoute permission="users"><UserCreationTemplates /></ProtectedRoute>}
        />

        {/* Admin - Roles */}
        <Route path="admin/roles" element={<ProtectedRoute permission="roles"><RolesList /></ProtectedRoute>} />
        <Route path="admin/roles/new" element={<ProtectedRoute permission="roles_add"><RoleForm /></ProtectedRoute>} />
        <Route path="admin/roles/:id/edit" element={<ProtectedRoute permission="roles_modify"><RoleForm /></ProtectedRoute>} />
        <Route path="admin/roles/:id" element={<ProtectedRoute permission="roles"><RoleDetail /></ProtectedRoute>} />

        {/* Admin - Groups */}
        <Route path="admin/groups" element={<ProtectedRoute permission="groups"><GroupsList /></ProtectedRoute>} />
        <Route path="admin/groups/new" element={<ProtectedRoute permission="groups_add"><GroupForm /></ProtectedRoute>} />
        <Route path="admin/groups/:id/edit" element={<ProtectedRoute permission="groups_modify"><GroupForm /></ProtectedRoute>} />
        <Route path="admin/groups/:id" element={<ProtectedRoute permission="groups"><GroupDetail /></ProtectedRoute>} />

        {/* Admin - Settings (Admin only) */}
        <Route path="admin/pto-requests" element={<ProtectedRoute permission="users"><TeamPtoApprovals adminView /></ProtectedRoute>} />
        <Route path="admin/settings" element={<ProtectedRoute permission="users"><Settings /></ProtectedRoute>} />
        <Route
          path="admin/all-statuses/:queueType"
          element={<ProtectedRoute permission="users"><AdminAllStatusesRouter /></ProtectedRoute>}
        />
        <Route path="admin/visibility-setup" element={<ProtectedRoute permission="users"><VisibilitySetup /></ProtectedRoute>} />
        <Route
          path="admin/customize/application-settings"
          element={<ProtectedRoute requireAdmin><CustomizeApplicationSettings /></ProtectedRoute>}
        />
        <Route path="admin/customize/fields" element={<ProtectedRoute requireAdmin><CustomFieldsAdmin /></ProtectedRoute>} />
        <Route
          path="admin/customize/relationships"
          element={<ProtectedRoute requireAdmin><ModuleRelationshipsAdmin /></ProtectedRoute>}
        />
        <Route path="admin/system-info" element={<ProtectedRoute permission="users"><SystemInfoPage /></ProtectedRoute>} />
        <Route path="admin/images" element={<ProtectedRoute requireAdmin><ImagesAdmin /></ProtectedRoute>} />
        <Route path="admin/audit-trail" element={<ProtectedRoute permission="users"><AdminAuditTrail /></ProtectedRoute>} />
        <Route path="admin/db-backup" element={<ProtectedRoute requireAdmin><DbBackupAdmin /></ProtectedRoute>} />
        <Route path="admin/document-backup" element={<ProtectedRoute requireAdmin><DocumentBackupAdmin /></ProtectedRoute>} />
        <Route path="admin/seed-data" element={<ProtectedRoute requireAdmin><AdminSeedData /></ProtectedRoute>} />
        <Route path="admin/demo-users-view" element={<ProtectedRoute requireAdmin><DemoUsersActualView /></ProtectedRoute>} />
        <Route path="admin/deleted-data" element={<ProtectedRoute requireAdmin><AdminDeletedData /></ProtectedRoute>} />
        <Route path="admin/archived-data" element={<ProtectedRoute requireAdmin><AdminArchivedData /></ProtectedRoute>} />
        <Route path="admin/data-clearance" element={<ProtectedRoute requireAdmin><AdminDataClearance /></ProtectedRoute>} />
        <Route path="admin/data-backups" element={<ProtectedRoute requireAdmin><AdminDataBackups /></ProtectedRoute>} />
        <Route
          path="admin/account-duplicate-requests"
          element={(
            <ProtectedRoute permission={['accounts_approve', 'accounts_modify']}>
              <AccountDuplicateRequests />
            </ProtectedRoute>
          )}
        />
        <Route path="admin/employee-settings/job-roles" element={<ProtectedRoute permission="users"><EmployeeJobRoles /></ProtectedRoute>} />
        <Route path="admin/employee-settings/titles" element={<ProtectedRoute permission="users"><EmployeeTitles /></ProtectedRoute>} />
        <Route path="admin/employee-settings/offered-titles" element={<ProtectedRoute permission="users"><EmployeeOfferedTitles /></ProtectedRoute>} />

        {/* Profile */}
        <Route path="profile" element={<Profile />} />

        {/* Punch Card */}
        <Route path="punchcard" element={<PunchCard />} />

        <Route path="my-items/pto" element={<ProtectedRoute permission="dashboard"><MyPtoApply /></ProtectedRoute>} />
        <Route path="my-items/my-account-status" element={<ProtectedRoute permission="accounts"><MyAccountStatus /></ProtectedRoute>} />

        {/* My Activities */}
        <Route path="activities" element={<MyActivities />} />
        <Route
          path="activities/all"
          element={<ProtectedRoute permission="all_activities_view"><AllActivities /></ProtectedRoute>}
        />

        {/* Reports — manager-style routes use backend RBAC; idle confidential route is permission-gated */}
        <Route path="reports/punchcard" element={<PunchCardReport />} />
        <Route path="reports/jobs" element={<JobsReport />} />
        <Route path="reports/activities" element={<ActivitiesReport />} />
        <Route path="reports/my-idle-time" element={<MyIdleTimeReport />} />
        <Route
          path="reports/my-team-idle-time"
          element={
            <ProtectedRoute permission="reports">
              <MyTeamIdleTimeReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="reports/idle-time"
          element={
            <ProtectedRoute permission="reports_confidential">
              <EmpIdleTimeReport />
            </ProtectedRoute>
          }
        />

        {/* Email Extractor */}
        <Route path="email-extractor" element={<ProtectedRoute permission="email_extractor"><EmailExtractorDashboard /></ProtectedRoute>} />
        <Route path="email-extractor/emails" element={<ProtectedRoute permission="email_extractor"><ExtractedEmails /></ProtectedRoute>} />
        <Route path="email-extractor/domains" element={<ProtectedRoute permission="email_extractor"><DomainExclusions /></ProtectedRoute>} />
        <Route path="email-extractor/import" element={<ProtectedRoute permission="email_extractor"><ManualEntry /></ProtectedRoute>} />
        <Route path="email-extractor/history" element={<ProtectedRoute permission="email_extractor"><ExtractionHistory /></ProtectedRoute>} />
        <Route path="email-extractor/pst" element={<ProtectedRoute permission="email_extractor"><PstUpload /></ProtectedRoute>} />
        <Route path="email-extractor/providers" element={<ProtectedRoute permission="email_extractor"><EmailProviders /></ProtectedRoute>} />

        {/* Extracted Data (libraries) */}
        <Route path="extracted-data/gsa-companies" element={<ProtectedRoute permission="extracted_data"><GsaCompanies /></ProtectedRoute>} />
        <Route path="extracted-data/us-universities" element={<ProtectedRoute permission="extracted_data"><UsUniversities /></ProtectedRoute>} />

        {/* Bench Sales — bench_sales_* or contacts_* (legacy) */}
        <Route path="bench-sales" element={<ProtectedRoute anyModule={BENCH_SALES_MODULES}><BenchesList /></ProtectedRoute>} />
        <Route path="bench-sales/available" element={<ProtectedRoute anyModule={BENCH_SALES_MODULES}><AvailableCandidates /></ProtectedRoute>} />
        <Route path="bench-sales/my-benches" element={<Navigate to="/my-items/my-benches" replace />} />
        <Route path="my-items/my-benches" element={<ProtectedRoute anyModule={BENCH_SALES_MODULES}><BenchesList /></ProtectedRoute>} />
        <Route path="my-team/bench" element={<ProtectedRoute anyModule={BENCH_SALES_MODULES}><BenchesList /></ProtectedRoute>} />
        <Route path="bench-sales/:id" element={<ProtectedRoute anyModule={BENCH_SALES_MODULES}><BenchDetails /></ProtectedRoute>} />

        {/* Digital Media */}
        <Route path="digital-media/social-registration" element={<SocialMediaRegistration />} />
        <Route path="digital-media/my-accounts" element={<MyAccounts />} />

        {/* Camera (workplace presence video) */}
        <Route path="camera" element={<ProtectedRoute permission="camera"><EmployeeCamera /></ProtectedRoute>} />
        <Route path="camera/admin" element={<ProtectedRoute permission="camera_watch"><AdminCameraDashboard /></ProtectedRoute>} />

        <Route path="chat" element={<ProtectedRoute permission="chat"><ChatHub /></ProtectedRoute>} />

        <Route path="my-team/camera" element={<ProtectedRoute permission="camera"><TeamCameraStream /></ProtectedRoute>} />
        <Route path="my-team/jobs" element={<ProtectedRoute permission="jobs"><MyTeamJobs /></ProtectedRoute>} />
        <Route path="my-team/dashboard" element={<ProtectedRoute permission="jobs"><MyTeamDashboard /></ProtectedRoute>} />
        <Route path="my-team/reportees" element={<ProtectedRoute permission="jobs"><MyTeamReportees /></ProtectedRoute>} />
        <Route path="my-team/employees/:id" element={<ProtectedRoute permission="jobs"><EmployeeDetails /></ProtectedRoute>} />
        <Route path="my-team/employees/:id/edit" element={<ProtectedRoute permission="jobs"><EmployeeForm /></ProtectedRoute>} />
        <Route
          path="my-team/okrs"
          element={
            <ProtectedRoute permission="okr">
              <OkrObjectivesList
                title="My Team OKRs"
                description="OKRs owned by your team. Use the setup module to create and manage definitions."
                allowManageActions={false}
              />
            </ProtectedRoute>
          }
        />
        <Route
          path="my-team/okr-scoreboard"
          element={
            <ProtectedRoute permission="okr">
              <OkrObjectivesList
                title="My Team OKRs Scoreboard"
                description="Scoreboard view across your team's OKRs."
                scoreboardMode
                allowManageActions={false}
              />
            </ProtectedRoute>
          }
        />

        {/* Help (module demos — erp4c-video-creator) */}
        <Route path="help/product" element={<ProtectedRoute><HelpProduct /></ProtectedRoute>} />
        <Route path="help/contacts" element={<ProtectedRoute permission="contacts"><HelpContacts /></ProtectedRoute>} />

        {/* Help Desk */}
        <Route path="helpdesk/my" element={<ProtectedRoute permission="helpdesk"><HelpDeskMyTickets /></ProtectedRoute>} />
        <Route path="helpdesk/team" element={<ProtectedRoute permission="helpdesk"><HelpDeskTeamTickets /></ProtectedRoute>} />
        <Route path="helpdesk/new" element={<ProtectedRoute permission="helpdesk"><HelpDeskNewTicket /></ProtectedRoute>} />
        <Route path="helpdesk/job-support-seeker" element={<ProtectedRoute permission="helpdesk"><HelpDeskJobSupportSeeker /></ProtectedRoute>} />
        <Route path="helpdesk/job-support-provider" element={<ProtectedRoute permission="helpdesk"><HelpDeskJobSupportProvider /></ProtectedRoute>} />
        <Route path="helpdesk/job-support" element={<ProtectedRoute permission="helpdesk"><HelpDeskJobSupportSeeker /></ProtectedRoute>} />
        <Route path="helpdesk/queue" element={<ProtectedRoute permission="helpdesk"><HelpDeskQueue /></ProtectedRoute>} />
        <Route path="helpdesk/:id" element={<ProtectedRoute permission="helpdesk"><HelpDeskTicketDetail /></ProtectedRoute>} />

        <Route path="admin/helpdesk-severities" element={<ProtectedRoute permission="helpdesk_admin"><AdminHelpdeskSeverities /></ProtectedRoute>} />
        <Route
          path="admin/help-setup"
          element={
            <ProtectedRoute requireAdmin>
              <AdminHelpSetup />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/training-administration"
          element={
            <ProtectedRoute permission="training_admin">
              <TrainingAdministration />
            </ProtectedRoute>
          }
        />

        {/* Immigration */}
        <Route path="immigration/process-h1" element={<ProtectedRoute permission="immigration"><ProcessH1List /></ProtectedRoute>} />
        <Route path="immigration/process-b1" element={<ProtectedRoute permission="immigration"><ProcessB1List /></ProtectedRoute>} />
        <Route path="immigration/process-gc" element={<ProtectedRoute permission="immigration"><ProcessGCList /></ProtectedRoute>} />
        <Route path="immigration/everify-existing" element={<ProtectedRoute permission="immigration"><EverifyExistingList /></ProtectedRoute>} />
        <Route path="immigration/everify-new" element={<ProtectedRoute permission="immigration"><EverifyNewList /></ProtectedRoute>} />

        {/* OKRs */}
        <Route
          path="my-items/okrs"
          element={
            <ProtectedRoute permission="okr">
              <OkrMy title="My OKRs" description="Objectives your manager assigned to you." />
            </ProtectedRoute>
          }
        />
        <Route
          path="my-items/okr-scoreboard"
          element={
            <ProtectedRoute permission="okr">
              <OkrMy title="My OKR Scoreboard" description="Scoreboard view for your assigned OKRs." scoreboardMode />
            </ProtectedRoute>
          }
        />
        <Route
          path="my-items/employee-map"
          element={
            <ProtectedRoute permission="okr">
              <OkrEmployeeMap />
            </ProtectedRoute>
          }
        />
        <Route path="okr/my" element={<ProtectedRoute permission="okr"><OkrMy /></ProtectedRoute>} />
        <Route path="okr/objectives" element={<ProtectedRoute permission="okr"><OkrObjectivesList /></ProtectedRoute>} />
        <Route path="okr/objectives/new" element={<ProtectedRoute permission="okr"><OkrObjectiveEditor /></ProtectedRoute>} />
        <Route path="okr/objectives/:id/edit" element={<ProtectedRoute permission="okr"><OkrObjectiveEditor /></ProtectedRoute>} />
        <Route path="okr/objectives/:id" element={<ProtectedRoute permission="okr"><OkrObjectiveDetail /></ProtectedRoute>} />
        <Route path="okr/categories" element={<ProtectedRoute permission="okr"><OkrCategories /></ProtectedRoute>} />
        <Route path="okr/key-results" element={<ProtectedRoute permission="okr"><KeyResultsMatrix /></ProtectedRoute>} />
        <Route path="okr/employee-map" element={<ProtectedRoute permission="okr"><OkrEmployeeMap /></ProtectedRoute>} />
        <Route
          path="okr/settings/target-frequency"
          element={
            <ProtectedRoute permission="okr">
              <OkrTargetFrequencySettings />
            </ProtectedRoute>
          }
        />
      </Route>

      <Route path="*" element={<DefaultRedirect />} />
    </Routes>
  );
}

export default App;
